// Phase 9 R2 — Restock dialog (positive qty + optional unit cost).
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useRestockInventory } from '@/hooks/inventory/useRestockInventory';
import type { InventoryItem } from '@/hooks/inventory/useInventoryItems';
import { useEffect } from 'react';

const schema = z.object({
  qty: z.coerce.number().positive('Quantity must be positive'),
  unit_cost: z
    .union([z.coerce.number().min(0), z.literal('').transform(() => undefined)])
    .optional(),
  notes: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

export function RestockDialog({
  item,
  open,
  onOpenChange,
}: {
  item: InventoryItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const restock = useRestockInventory();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { qty: 0, notes: '' },
  });

  useEffect(() => {
    if (open) form.reset({ qty: 0, notes: '' });
  }, [open]);

  const watchQty = form.watch('qty');
  const onHand = item?.quantity_on_hand ?? 0;
  const previewAfter = onHand + Number(watchQty || 0);

  const submit = async (values: FormValues) => {
    if (!item) return;
    await restock.mutateAsync({
      id: item.id,
      qty: values.qty,
      unit_cost: typeof values.unit_cost === 'number' ? values.unit_cost : null,
      notes: values.notes || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Restock</DialogTitle>
          <DialogDescription>
            Increase on-hand quantity from a vendor delivery. Optionally update unit cost.
          </DialogDescription>
        </DialogHeader>

        {item && (
          <div className="rounded-md border p-3 text-sm">
            <div className="font-medium">{item.name}</div>
            <div className="text-muted-foreground">{item.part_number}</div>
            <div className="mt-2">
              On Hand: <span className="font-mono">{onHand}</span>
              {' → '}
              <span className="font-mono">{previewAfter}</span>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <FormField name="qty" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Quantity to Add</FormLabel>
                <FormControl><Input type="number" step="1" min="1" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField name="unit_cost" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>New Unit Cost (optional)</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" placeholder="Leave blank to keep existing" {...field} />
                </FormControl>
              </FormItem>
            )} />
            <FormField name="notes" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea rows={2} placeholder="PO number, vendor, etc." {...field} />
                </FormControl>
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={restock.isPending}>
                {restock.isPending ? 'Submitting…' : 'Record Restock'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
